import { useMemo } from 'react';
import { MultiSelectDropdown } from '../unified/MultiSelectDropdown';
import { SalesClientSearch } from './SalesClientSearch';
import type { SalesFilterOptions } from '../../api/types';
import type { SalesFilterState } from './salesViewFilters';

interface SalesEntryFiltersProps {
  options: SalesFilterOptions | null;
  filters: SalesFilterState;
  onFiltersChange: (next: SalesFilterState) => void;
  /** The phone keeps search in its top bar, so the filter sheet renders the dropdowns alone. */
  showSearch?: boolean;
  stacked?: boolean;
}

type Dim = 'manager' | 'owner' | 'marketCenter';

/**
 * Entry filter row (§6.2), trimmed to what a CSE actually plans a day with:
 * manager, rep and market center.
 *
 * CSE Director, Dealer Group, State and City were dropped as noise for this
 * audience. All four remain filterable on the API, so nothing is lost for a
 * caller that wants them.
 *
 * The rep filter keys on `cse_territory_owner`, NOT the territory id: reps and
 * managers think in people, and owner <-> territory is strictly 1:1 (measured
 * 219 <-> 219, none spanning two), so the two carry identical meaning.
 * Unassigned territories show as their "OPEN CSE ..." placeholder rather than
 * dropping out of the list.
 *
 * The three dropdowns CASCADE, matching Client View: each list is narrowed by
 * the OTHER two selections, so picking a manager leaves only their reps and
 * market centers. A value that is already selected is always kept in its own
 * list -- otherwise a selection made before a narrowing would vanish from the
 * row and become impossible to clear.
 *
 * Search resolves to CLIENTS. Sales View v1 has no VIN list or VIN drawer, so
 * the dropdown offers clients and dealer groups only, rather than leaving a rep
 * to paste a VIN and conclude the tool is broken.
 */
export function SalesEntryFilters({
  options,
  filters,
  onFiltersChange,
  showSearch = true,
  stacked = false,
}: SalesEntryFiltersProps) {
  const set = (key: keyof SalesFilterState) => (values: string[]) =>
    onFiltersChange({ ...filters, [key]: values });

  const cascaded = useMemo(() => {
    const mapping = options?.mapping ?? [];
    const full = {
      manager: options?.cse_managers ?? [],
      owner: options?.cse_territory_owners ?? [],
      marketCenter: options?.market_centers ?? [],
    };
    const selected: Record<Dim, string[]> = {
      manager: filters.cse_manager,
      owner: filters.cse_territory_owner,
      marketCenter: filters.market_center,
    };

    // No mapping (older API, or an empty book) means no narrowing. Fall back to
    // the flat lists rather than emptying the row.
    if (mapping.length === 0) return full;

    const narrow = (dim: Dim): string[] => {
      let rows = mapping;
      if (dim !== 'manager' && selected.manager.length > 0) {
        rows = rows.filter((m) => m.cse_manager && selected.manager.includes(m.cse_manager));
      }
      if (dim !== 'owner' && selected.owner.length > 0) {
        rows = rows.filter((m) => m.cse_territory_owner && selected.owner.includes(m.cse_territory_owner));
      }
      if (dim !== 'marketCenter' && selected.marketCenter.length > 0) {
        rows = rows.filter((m) => m.market_center && selected.marketCenter.includes(m.market_center));
      }
      const key = dim === 'manager' ? 'cse_manager' : dim === 'owner' ? 'cse_territory_owner' : 'market_center';
      const values = new Set<string>();
      for (const row of rows) {
        const v = row[key];
        if (v) values.add(v);
      }
      for (const v of selected[dim]) values.add(v);
      return [...values].sort();
    };

    return {
      manager: narrow('manager'),
      owner: narrow('owner'),
      marketCenter: narrow('marketCenter'),
    };
  }, [
    options,
    filters.cse_manager,
    filters.cse_territory_owner,
    filters.market_center,
  ]);

  return (
    <div className={`sv-filters-row${stacked ? ' sv-filters-row--stack' : ''}`}>
      <MultiSelectDropdown
        label="CSE Manager"
        options={cascaded.manager}
        values={filters.cse_manager}
        onChange={set('cse_manager')}
      />
      <MultiSelectDropdown
        label="CSE Rep"
        options={cascaded.owner}
        values={filters.cse_territory_owner}
        onChange={set('cse_territory_owner')}
      />
      <MultiSelectDropdown
        label="Market Center"
        options={cascaded.marketCenter}
        values={filters.market_center}
        onChange={set('market_center')}
      />
      {showSearch && (
        <SalesClientSearch
          selected={filters.client_token}
          onChange={set('client_token')}
        />
      )}
    </div>
  );
}

export default SalesEntryFilters;
